// ICIC_WINNER_CERTIFICATE_IN_APP_V1
// ICIC_POSTER_APP_INTEGRATION_V1
import { useEffect, useMemo, useState } from 'react';
import {
  loadIcicPosterState,
  submitIcicPoster
} from '../utils/icic';
import './ICICPortal.css';

const countWords = value => {
  const text = String(value || '').trim();
  return text ? text.split(/\s+/).length : 0;
};
const hasChinese = value =>
  /[\u3400-\u9fff]/.test(String(value || ''));
const cleanStatus = value => {
  const text = String(value || 'submitted').replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};
const blankStudent = (student = {}) => ({
  name_en: student.name || '',
  name_zh: '',
  student_id: student.studentId || '',
  school: student.school || '',
  email: student.email || '',
  phone: ''
});
const blankInstructor = () => ({
  name_en: '',
  name_zh: '',
  school: '',
  email: ''
});

function ExternalAction({ href, children, secondary = false }) {
  if (!href) return null;
  return (
    <a
      className={
        secondary
          ? 'icic-action-link secondary'
          : 'icic-action-link'
      }
      href={href}
      target="_blank"
      rel="noreferrer"
    >
      {children}
    </a>
  );
}

export default function ICICPosterSubmission({
  session,
  onBack
}) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [certificateUrl, setCertificateUrl] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [language, setLanguage] = useState('English');
  const [division, setDivision] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [title, setTitle] = useState('');
  const [abstract, setAbstract] = useState('');
  const [students, setStudents] = useState([blankStudent()]);
  const [instructor, setInstructor] = useState(
    blankInstructor()
  );

  async function load() {
    setLoading(true);
    setError('');
    try {
      const result = await loadIcicPosterState(session);
      setState(result);
      setStudents([blankStudent(result?.student || {})]);
      const hasPosters =
        Array.isArray(result?.posters) &&
        result.posters.length > 0;
      setShowForm(result?.submitOpen === true && !hasPosters);
    } catch (err) {
      setError(
        err?.message ||
        'ICIC poster information could not be loaded.'
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let active = true;
    setLoading(true);

    loadIcicPosterState(session)
      .then(result => {
        if (!active) return;
        setState(result);
        setStudents([blankStudent(result?.student || {})]);
        const hasPosters =
          Array.isArray(result?.posters) &&
          result.posters.length > 0;
        setShowForm(result?.submitOpen === true && !hasPosters);
      })
      .catch(err => {
        if (active) {
          setError(
            err?.message ||
            'ICIC poster information could not be loaded.'
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [session]);

  const categories = useMemo(
    () =>
      Array.isArray(state?.categories)
        ? state.categories
        : [],
    [state?.categories]
  );

  const posters = Array.isArray(state?.posters)
    ? state.posters
    : [];

  const selectedCategory = useMemo(
    () =>
      categories.find(
        category => String(category.id) === String(categoryId)
      ) || null,
    [categories, categoryId]
  );
  const competition = state?.competition || {};

  function resetForm() {
    setLanguage('English');
    setDivision('');
    setCategoryId('');
    setTitle('');
    setAbstract('');
    setStudents([blankStudent(state?.student || {})]);
    setInstructor(blankInstructor());
  }

  function updateStudent(index, field, value) {
    setStudents(current =>
      current.map((student, i) =>
        i === index
          ? { ...student, [field]: value }
          : student
      )
    );
  }

  function updateInstructor(field, value) {
    setInstructor(current => ({
      ...current,
      [field]: value
    }));
  }

  async function submit(event) {
    event.preventDefault();
    if (submitting || !state?.submitOpen) return;

    setError('');
    setNotice('');

    if (
      !division ||
      !categoryId ||
      !title.trim() ||
      !abstract.trim() ||
      students.some(
        student =>
          !student.name_en.trim() ||
          !student.student_id.trim() ||
          !student.school.trim() ||
          !student.email.trim()
      )
    ) {
      setError('Please complete all required fields.');
      return;
    }

    if (
      hasChinese(title) ||
      hasChinese(abstract) ||
      students.some(student => hasChinese(student.name_en)) ||
      hasChinese(instructor.name_en)
    ) {
      setError(
        'The title, abstract, and English names must be written in English.'
      );
      return;
    }

    if (
      countWords(abstract) >
      Number(state?.submission?.wordLimit || 300)
    ) {
      setError('The abstract exceeds the word limit.');
      return;
    }

    const instructorUsed = Object.values(instructor).some(
      value => String(value || '').trim()
    );

    if (
      instructorUsed &&
      (
        !instructor.name_en.trim() ||
        !instructor.school.trim() ||
        !instructor.email.trim()
      )
    ) {
      setError(
        'If you add an instructor, enter their English name, school, and email.'
      );
      return;
    }

    setSubmitting(true);

    try {
      const result = await submitIcicPoster(session, {
        language,
        division,
        category_id: categoryId,
        title: title.trim(),
        abstract: abstract.trim(),
        students: students.map(student => ({
          ...student,
          name_en: student.name_en.trim(),
          name_zh: student.name_zh.trim(),
          student_id: student.student_id.trim(),
          school: student.school.trim(),
          email: student.email.trim(),
          phone: student.phone.trim()
        })),
        instructor: {
          name_en: instructor.name_en.trim(),
          name_zh: instructor.name_zh.trim(),
          school: instructor.school.trim(),
          email: instructor.email.trim()
        }
      });

      setNotice(
        result?.receipt?.entry_code
          ? 'Submission received: ' + result.receipt.entry_code
          : 'Submission received.'
      );
      resetForm();
      setShowForm(false);
      await load();
    } catch (err) {
      setError(
        err?.message ||
        'The poster proposal could not be submitted.'
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (certificateUrl) {
    return (
      <div className="icic-poster-app">
        <div className="icic-poster-app-inner">
          <header className="icic-poster-app-header">
            <button
              type="button"
              className="icic-back"
              onClick={() => setCertificateUrl('')}
            >
              ‹ Back to My ICIC Posters
            </button>

            <div className="icic-kicker">
              ICIC Award
            </div>

            <h1>
              Winner Certificate
            </h1>
          </header>

          <div className="icic-poster-app-card">
            <a
              className="icic-action-link secondary"
              href={certificateUrl}
              target="_blank"
              rel="noreferrer"
            >
              Open certificate separately
            </a>
          </div>

          <iframe
            title="ICIC winner certificate"
            src={certificateUrl}
            style={{
              display: 'block',
              width: '100%',
              height: '78vh',
              border:
                '1px solid #d9e2ec',
              borderRadius: 10,
              background: '#fff'
            }}
          />
        </div>
      </div>
    );
  }


  return (
    <div className="icic-poster-app">
      <div className="icic-poster-app-inner">
        <header className="icic-poster-app-header">
          {onBack && (
            <button
              type="button"
              className="icic-back"
              onClick={onBack}
            >
              ‹ Back
            </button>
          )}

          <div className="icic-kicker">
            {state?.conference?.name || 'ICIC'}
          </div>

          <h1>ICIC Poster Submission</h1>
        </header>

        {error && (
          <div className="icic-poster-app-alert">
            {error}
          </div>
        )}
        {notice && (
          <div className="icic-poster-app-success">
            {notice}
          </div>
        )}

        {loading ? (
          <div className="icic-loading">
            Loading ICIC poster information…
          </div>
        ) : !state?.available ? (
          <div className="icic-empty">
            ICIC poster submission is not currently available in the app.
          </div>
        ) : (
          <>
            {posters.map(poster => (
              <article
                className="icic-poster-app-card"
                key={poster.id}
              >
                <span className="icic-kicker">
                  {[
                    poster.entryCode,
                    poster.category,
                    poster.division
                  ].filter(Boolean).join(' · ')}
                </span>

                <h2>{poster.title}</h2>

                <div className="icic-poster-status-row">
                  <span>{cleanStatus(poster.status)}</span>
                  {poster.withdrawn && <span>Withdrawn</span>}
                  {poster?.upload?.received && (
                    <span>Final poster received</span>
                  )}
                  {(poster.awards || []).map((award, index) => (
                    <span key={index}>
                      {cleanStatus(award.type)}
                      {award.rank ? ' ' + award.rank : ''}
                    </span>
                  ))}
                </div>

                {(poster.awards || [])
                  .filter(
                    award =>
                      !!award.certificateUrl
                  )
                  .map(award => (
                    <div
                      className="icic-poster-form-actions"
                      key={
                        'certificate-' +
                        award.id
                      }
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setCertificateUrl(
                            award.certificateUrl
                          )
                        }
                      >
                        View Certificate
                      </button>
                    </div>
                  ))}

                {poster.abstract && <p>{poster.abstract}</p>}

                <div className="icic-poster-author-list">
                  {(poster.authors || []).map(author => (
                    <div
                      className="icic-poster-author"
                      key={author.id}
                    >
                      <strong>
                        {author.name}
                        {author.isCurrentStudent ? ' · You' : ''}
                      </strong>
                      <span>{author.school}</span>
                      <span>
                        {author.consentComplete
                          ? 'Consent received'
                          : (
                              poster.status === 'accepted' ||
                              poster.status === 'poster_received'
                            )
                              ? 'Consent outstanding'
                              : 'Consent not yet required'}
                      </span>

                      {author.consentUrl && (
                        <ExternalAction
                          href={author.consentUrl}
                          secondary
                        >
                          Complete my consent form
                        </ExternalAction>
                      )}
                    </div>
                  ))}
                </div>

                {poster?.upload?.received ? (
                  <p>
                    Final file: {
                      poster.upload.filename || 'received'
                    }
                    {poster.upload.qualityStatus
                      ? ' · ' + poster.upload.qualityStatus
                      : ''}
                  </p>
                ) : poster.uploadUrl ? (
                  <ExternalAction href={poster.uploadUrl}>
                    Upload final poster
                  </ExternalAction>
                ) : null}
              </article>
            ))}

            {state.submitOpen && (
              <section className="icic-poster-app-card">
                {!showForm ? (
                  <>
                    <h2>Submit another poster</h2>
                    <p>
                      You can submit another project even if you are already listed on an existing proposal.
                    </p>
                    <div className="icic-poster-form-actions">
                      <button
                        type="button"
                        onClick={() => setShowForm(true)}
                      >
                        Submit another poster
                      </button>
                    </div>
                  </>
                ) : (
                  <form
                    className="icic-poster-form"
                    onSubmit={submit}
                  >
                    <h2>
                      {posters.length > 0
                        ? 'Submit another poster'
                        : 'Submit a Poster to ICIC'}
                    </h2>

                    <p>
                      Proposal deadline: {
                        state?.submission?.closesOn || 'Not set'
                      }. Abstract limit: {
                        state?.submission?.wordLimit || 300
                      } words.
                    </p>

                    <div className="icic-poster-form-grid">
                      <label>
                        Submission language
                        <select
                          value={language}
                          onChange={event =>
                            setLanguage(event.target.value)
                          }
                        >
                          <option>English</option>
                          <option>Chinese</option>
                        </select>
                      </label>

                      <label>
                        Division *
                        <select
                          required
                          value={division}
                          onChange={event =>
                            setDivision(event.target.value)
                          }
                        >
                          <option value="">Select division</option>
                          <option value="High School">
                            High School
                          </option>
                          <option value="University">
                            University
                          </option>
                        </select>
                      </label>
                    </div>

                    <label>
                      Project Category *
                      <select
                        required
                        value={categoryId}
                        onChange={event =>
                          setCategoryId(event.target.value)
                        }
                      >
                        <option value="">Select category</option>
                        {categories.map(category => (
                          <option
                            key={category.id}
                            value={category.id}
                          >
                            {language === 'Chinese'
                              ? (
                                  category.name_zh ||
                                  category.name_en
                                )
                              : category.name_en}
                          </option>
                        ))}
                      </select>
                    </label>

                    {selectedCategory && (
                      <div className="icic-poster-author">
                        <strong>
                          {language === 'Chinese'
                            ? (
                                selectedCategory.name_zh ||
                                selectedCategory.name_en
                              )
                            : selectedCategory.name_en}
                        </strong>
                        <span>
                          {language === 'Chinese'
                            ? (
                                selectedCategory.guidance_zh ||
                                selectedCategory.guidance_en
                              )
                            : selectedCategory.guidance_en}
                        </span>
                      </div>
                    )}

                    <label>
                      Title (English) *
                      <input
                        required
                        value={title}
                        onChange={event =>
                          setTitle(event.target.value)
                        }
                      />
                    </label>

                    <label>
                      Abstract (English) *
                      <textarea
                        required
                        rows={6}
                        value={abstract}
                        onChange={event =>
                          setAbstract(event.target.value)
                        }
                      />
                    </label>

                    <div className="icic-poster-word-count">
                      {countWords(abstract)} / {
                        state?.submission?.wordLimit || 300
                      } words
                    </div>

                    {students.map((student, index) => (
                      <section
                        className="icic-poster-student-block"
                        key={index}
                      >
                        <h3>Student {index + 1}</h3>

                        <div className="icic-poster-form-grid">
                          {[
                            ['school', 'School *', 'text', true],
                            ['name_en', 'English name *', 'text', true],
                            ['name_zh', 'Chinese name', 'text', false],
                            ['student_id', 'Student ID *', 'text', true],
                            ['email', 'Email *', 'email', true],
                            ['phone', 'Phone', 'text', false]
                          ].map(([field, label, type, required]) => (
                            <label key={field}>
                              {label}
                              <input
                                type={type}
                                required={required}
                                value={student[field]}
                                onChange={event =>
                                  updateStudent(
                                    index,
                                    field,
                                    event.target.value
                                  )
                                }
                              />
                            </label>
                          ))}
                        </div>

                        {index === students.length - 1 &&
                          index > 0 && (
                          <div className="icic-poster-form-actions">
                            <button
                              type="button"
                              className="secondary"
                              onClick={() =>
                                setStudents(current =>
                                  current.slice(0, -1)
                                )
                              }
                            >
                              Remove student
                            </button>
                          </div>
                        )}
                      </section>
                    ))}

                    {students.length < 4 && (
                      <div className="icic-poster-form-actions">
                        <button
                          type="button"
                          className="secondary"
                          onClick={() =>
                            setStudents(current => [
                              ...current,
                              blankStudent()
                            ])
                          }
                        >
                          Add another student
                        </button>
                      </div>
                    )}

                    <h3>Instructor Information (optional)</h3>

                    <div className="icic-poster-form-grid">
                      {[
                        ['name_en', 'English name', 'text'],
                        ['name_zh', 'Chinese name', 'text'],
                        ['school', 'School', 'text'],
                        ['email', 'Email', 'email']
                      ].map(([field, label, type]) => (
                        <label key={field}>
                          {label}
                          <input
                            type={type}
                            value={instructor[field]}
                            onChange={event =>
                              updateInstructor(
                                field,
                                event.target.value
                              )
                            }
                          />
                        </label>
                      ))}
                    </div>

                    <div className="icic-poster-form-actions">
                      <button
                        type="submit"
                        disabled={submitting}
                      >
                        {submitting
                          ? 'Submitting…'
                          : 'Submit proposal'}
                      </button>

                      {posters.length > 0 && (
                        <button
                          type="button"
                          className="secondary"
                          disabled={submitting}
                          onClick={() => {
                            resetForm();
                            setShowForm(false);
                          }}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </form>
                )}
              </section>
            )}

            {!state.submitOpen && posters.length === 0 && (
              <div className="icic-empty">
                Poster proposals are not currently open in the app.
              </div>
            )}

            {(
              competition?.peopleChoice?.configured ||
              (
                Array.isArray(competition?.judges) &&
                competition.judges.length > 0
              )
            ) && (
              <section className="icic-poster-app-card">
                <h2>Voting</h2>

                {competition?.peopleChoice?.configured && (
                  <>
                    <p>
                      People’s Choice: {
                        competition.peopleChoice.open
                          ? 'Open now'
                          : (
                              [
                                competition.peopleChoice.date,
                                competition.peopleChoice.opensAt &&
                                competition.peopleChoice.closesAt
                                  ? (
                                      competition.peopleChoice.opensAt +
                                      '–' +
                                      competition.peopleChoice.closesAt
                                    )
                                  : null
                              ]
                                .filter(Boolean)
                                .join(' · ')
                            )
                      }
                    </p>

                    {competition.peopleChoice.open && (
                      <ExternalAction
                        href={competition.peopleChoice.url}
                      >
                        Open People’s Choice voting
                      </ExternalAction>
                    )}
                  </>
                )}

                {(competition?.judges || []).map(judge => (
                  <div key={judge.id}>
                    <p>
                      Judge: {judge.category} · {
                        judge.open
                          ? 'Open'
                          : 'Not currently open'
                      }
                    </p>

                    {judge.open && (
                      <ExternalAction href={judge.url}>
                        Open judging form
                      </ExternalAction>
                    )}
                  </div>
                ))}
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
